import { useCallback } from "react";
import toast from "react-hot-toast";

// Accept any form's setError — using `any` avoids schema field constraints
type SetErrorFn = (field: any, error: { type: string; message: string }) => void;

/**
 * Extracts backend field-level errors from an Axios error response
 * and maps them onto react-hook-form via setError().
 * Also shows a toast for generic server errors.
 */
export function useFormValidation() {
    const handleServerErrors = useCallback(
        (axiosError: any, setError: SetErrorFn, errorMessage?: string) => {
            const data = axiosError?.response?.data;

            if (!data) {
                toast.error(errorMessage || "An unexpected error occurred");
                return;
            }

            // If the backend returned structured field errors
            if (data.fields && typeof data.fields === "object") {
                let firstField: string | null = null;
                for (const [field, message] of Object.entries(data.fields)) {
                    setError(field as any, {
                        type: "server",
                        message: message as string,
                    });
                    if (!firstField) firstField = field;
                }
                // Scroll to first errored field
                if (firstField) {
                    const el =
                        document.querySelector(`[name="${firstField}"]`) ||
                        document.querySelector(`[data-field="${firstField}"]`);
                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                toast.error(errorMessage || "Please fix the highlighted fields");
                return;
            }

            // already_exists — try to map to the affected field
            if (data.error === "already_exists" && data.field) {
                setError(data.field, {
                    type: "server",
                    message: "This value is already in use",
                });
                toast.error("That value is already taken, please choose another");
                return;
            }

            // Fallback generic toast
            const msg = data.message || data.error || errorMessage || "Failed to save";
            toast.error(msg);
        },
        []
    );

    return { handleServerErrors };
}
